import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import Cookies from 'universal-cookie';
import { fetchUser } from '../api/FetchUser';
import { Ab } from '../interfaces/abTypes';
import { parseUserProfileFCString, FirmAccess } from '@arkitektbedriftene/fe-lib/oidc';
import { useAuthContext, userManager } from '../Auth';
import { setAiUser } from '../AppInsightsInit';
import AppStore from '../legacy/stores/AppStore';
import { toast } from 'react-hot-toast';

interface UserFirmContextType {
  setUser: (user: Ab.User) => void;
  user?: Ab.User;
  selectedFirm?: FirmAccess;
  setSelectedFirm: (firm: FirmAccess) => void;
  refreshUser: (retryCount?: number) => Promise<void>;
  loading: boolean;
}

const UserFirmContext = createContext<UserFirmContextType | undefined>(undefined);

export const UserFirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { state } = useAuthContext();
  const [user, setUser] = useState<Ab.User>();
  const [selectedFirm, setSelectedFirmState] = useState<FirmAccess>();
  const [loading, setLoading] = useState(true);

  // Silent logout function that doesn't redirect to login page
  const silentLogout = useCallback(async () => {
    try {
      // Clear user data
      setUser(undefined);
      setSelectedFirmState(undefined);
      AppStore.setUser(undefined);
      
      // Clear cookies
      const cookies = new Cookies();
      cookies.remove('currentCompany', { path: '/' });
      
      // Perform silent signout
      await userManager.signoutSilent({ post_logout_redirect_uri: window.location.origin });
      // Force a full page reload to reset the application state
      window.location.href = '/';
    } catch (error) {
      console.error('Error during silent logout:', error);
      // If silent logout fails, fall back to redirect
      userManager.signoutRedirect({ post_logout_redirect_uri: window.location.origin });
    }
  }, []);

  // Main function to fetch and set user data with retry logic
  const fetchAndSetUser = useCallback(async (retryCount = 0) => {
    if (!state?.user?.access_token) return;

    const MAX_RETRIES = 2;
    const RETRY_DELAY = 1000; // 1 second

    try {
      setLoading(true);
      const userData = await fetchUser();
      const firms = parseUserProfileFCString(userData.fc);
      const updatedUser = { ...userData, firmAccessList: firms };
      
      setUser(updatedUser);
      AppStore.setUser(updatedUser);
      
      const cookies = new Cookies();
      const storedFirmId = cookies.get('currentCompany');
      const firm = firms.find(f => f.CustomerFirmId === storedFirmId) || firms[0];
      if (firm) {
        setSelectedFirmState(firm);
        cookies.set('currentCompany', firm.CustomerFirmId, { 
          path: '/',
          sameSite: 'lax',
          secure: window.location.protocol === 'https:'
        });
        setAiUser(updatedUser.email, firm.CustomerFirmId);
      }
    } catch (err) {
      if (err instanceof Error && err.message.includes("Impersonationexpired")) {
        return;
      }
      
      console.error('Error fetching user (attempt ' + (retryCount + 1) + '):', err);
      
      // If we have retries left, try again
      if (retryCount < MAX_RETRIES) {
        console.log(`Retrying in ${RETRY_DELAY}ms... (${retryCount + 1}/${MAX_RETRIES})`);
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
        return fetchAndSetUser(retryCount + 1);
      }
      
      // If we've exhausted retries, show error and log out
      const errorMessage = 'Kunne ikke hente brukerinfo. Du blir logget ut.';
      console.error(errorMessage, err);
      
      // Show a toast instead of an alert
      toast.error(errorMessage);
      
      // Use silent logout instead of redirect
      await silentLogout();
    } finally {
      setLoading(false);
    }
  }, [state?.user?.access_token, silentLogout]);

  // Set up effect to fetch user data when access token changes
  useEffect(() => {
    fetchAndSetUser();
  }, [fetchAndSetUser]);

  const setSelectedFirm = useCallback((firm: FirmAccess) => {
    setSelectedFirmState(firm);
    const cookies = new Cookies();
    cookies.set('currentCompany', firm.CustomerFirmId, { 
      path: '/',
      sameSite: 'lax',
      secure: window.location.protocol === 'https:'
    });
  }, []);

  const contextValue = useMemo(() => ({
    user,
    selectedFirm,
    setSelectedFirm,
    loading,
    setUser,
    refreshUser: fetchAndSetUser
  }), [user, selectedFirm, loading, setSelectedFirm, fetchAndSetUser]);

  return (
    <UserFirmContext.Provider value={contextValue}>
      {children}
    </UserFirmContext.Provider>
  );
};

export const useUserFirmContext = () => {
  const context = useContext(UserFirmContext);
  if (!context) {
    throw new Error("useUserFirmContext must be used within a UserFirmProvider");
  }
  return context;
};